import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaTimes,
  FaQuestionCircle,
  FaUser,
  FaCalendar,
  FaIdCard,
  FaPhone,
  FaEnvelope,
  FaTag,
  FaDownload,
  FaEdit,
  FaTrash,
  FaCheck,
  FaFileAlt,
  FaBuilding,
} from "react-icons/fa";
import { BsFillQuestionCircleFill } from "react-icons/bs";
import CommonModal from "./CommonModal";
import {
  byteCount,
  catchError,
  changeState,
  commonModalSetting,
} from "JS/common";
import { answerHandling, getInquiryDetail, getFileDownload } from "JS/API";

// 디자인 시스템 임포트
import { designTokens, commonStyles, badgeStyles } from "utils/designSystem";
import { detailModalStyles } from "utils/modalDesignSystem";

const InquiryDetail = ({ inquiryId, setModal }) => {
  let prevent = false;
  const [byte, setByte] = useState(0);
  const [alert, setAlert] = useState("");
  const [edit, setEdit] = useState(false);
  const [info, setInfo] = useState({
    service_code: "",
    user_name: "",
    created_at: "",
    title: "",
    context: "",
    status: false,
    answer: "",
    user_id: "",
    name: "",
    phone: "",
    email: "",
    inquiry_type: "",
    inquiry_reason: "",
    apply_type: "",
    applicant_type: "",
    mark_name: "",
    files: [],
    is_auto: false,
  });
  const [alertBox, setAlertBox] = useState({
    mode: "",
    context: "",
    bool: false,
  });
  const navigate = useNavigate();

  // 스타일 정의
  const modalStyles = {
    overlay: detailModalStyles.overlay,
    container: {
      backgroundColor: designTokens.colors.surface,
      borderRadius: designTokens.borderRadius.xl,
      boxShadow: designTokens.shadows.lg,
      width: '100%',
      maxWidth: '1000px',
      maxHeight: '90vh',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    },
    header: detailModalStyles.header,
    title: detailModalStyles.title,
    closeButton: detailModalStyles.closeButton,
    content: detailModalStyles.content,
    statusHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: designTokens.spacing.xl,
      padding: designTokens.spacing.lg,
      backgroundColor: designTokens.colors.background,
      borderRadius: designTokens.borderRadius.lg,
      border: `1px solid ${designTokens.colors.border}`
    },
    dateInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: designTokens.spacing.sm,
      fontSize: '14px',
      color: designTokens.colors.text.secondary
    },
    memberBadge: {
      ...badgeStyles.base,
      fontSize: '12px',
      fontWeight: '600'
    },
    section: detailModalStyles.section,
    sectionTitle: {
      display: 'flex',
      alignItems: 'center',
      gap: designTokens.spacing.md,
      fontSize: '16px',
      fontWeight: '600',
      color: designTokens.colors.text.primary,
      marginBottom: designTokens.spacing.lg,
      padding: `${designTokens.spacing.md} 0`,
      borderBottom: `2px solid ${designTokens.colors.primary}`
    },
    inquiryTypeOptions: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: designTokens.spacing.md,
      marginBottom: designTokens.spacing.lg,
      marginLeft: designTokens.spacing.xl
    },
    radioOption: {
      display: 'flex',
      alignItems: 'center',
      gap: designTokens.spacing.sm,
      padding: `${designTokens.spacing.sm} ${designTokens.spacing.md}`,
      borderRadius: designTokens.borderRadius.md,
      border: `1px solid ${designTokens.colors.borderLight}`,
      backgroundColor: designTokens.colors.surface,
      fontSize: '13px'
    },
    radioOptionSelected: {
      backgroundColor: designTokens.colors.primary,
      color: designTokens.colors.surface,
      borderColor: designTokens.colors.primary
    },
    radioCircle: {
      width: '12px',
      height: '12px',
      borderRadius: '50%',
      border: `2px solid currentColor`,
      position: 'relative'
    },
    radioCircleSelected: {
      '::after': {
        content: '""',
        position: 'absolute',
        top: '2px',
        left: '2px',
        width: '4px',
        height: '4px',
        borderRadius: '50%',
        backgroundColor: 'currentColor'
      }
    },
    infoGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: designTokens.spacing.lg
    },
    infoRow: {
      display: 'flex',
      alignItems: 'center',
      padding: designTokens.spacing.md,
      backgroundColor: designTokens.colors.background,
      borderRadius: designTokens.borderRadius.md,
      border: `1px solid ${designTokens.colors.borderLight}`
    },
    infoLabel: {
      display: 'flex',
      alignItems: 'center',
      gap: designTokens.spacing.sm,
      fontSize: '14px',
      fontWeight: '600',
      color: designTokens.colors.text.secondary,
      minWidth: '120px'
    },
    infoValue: {
      fontSize: '14px',
      color: designTokens.colors.text.primary,
      fontWeight: '500',
      flex: 1
    },
    questionSection: {
      ...commonStyles.card,
      padding: designTokens.spacing.xl,
      marginBottom: designTokens.spacing.xl
    },
    questionHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: designTokens.spacing.lg
    },
    questionTitle: {
      fontSize: '16px',
      fontWeight: '600',
      color: designTokens.colors.text.primary
    },
    downloadButton: {
      ...commonStyles.buttonBase,
      backgroundColor: designTokens.colors.success,
      color: designTokens.colors.surface,
      padding: `${designTokens.spacing.sm} ${designTokens.spacing.md}`,
      fontSize: '12px'
    },
    questionContent: {
      padding: designTokens.spacing.lg,
      backgroundColor: designTokens.colors.background,
      borderRadius: designTokens.borderRadius.lg,
      border: `1px solid ${designTokens.colors.borderLight}`,
      lineHeight: '1.6',
      fontSize: '14px',
      color: designTokens.colors.text.primary,
      minHeight: '100px',
      whiteSpace: 'pre-wrap'
    },
    answerSection: {
      ...commonStyles.card,
      padding: designTokens.spacing.xl
    },
    answerTextarea: {
      width: '100%',
      minHeight: '200px',
      maxHeight: '400px',
      padding: designTokens.spacing.md,
      border: `1px solid ${designTokens.colors.border}`,
      borderRadius: designTokens.borderRadius.lg,
      fontSize: '14px',
      lineHeight: '1.6',
      resize: 'vertical',
      fontFamily: 'inherit'
    },
    answerContent: {
      padding: designTokens.spacing.lg,
      backgroundColor: designTokens.colors.background,
      borderRadius: designTokens.borderRadius.lg,
      border: `1px solid ${designTokens.colors.borderLight}`,
      lineHeight: '1.6',
      fontSize: '14px',
      color: designTokens.colors.text.primary,
      maxHeight: '400px',
      overflow: 'auto',
      whiteSpace: 'pre-wrap'
    },
    footer: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginTop: designTokens.spacing.lg,
      padding: designTokens.spacing.lg,
      backgroundColor: designTokens.colors.background,
      borderRadius: designTokens.borderRadius.lg
    },
    byteCount: {
      fontSize: '12px',
      color: designTokens.colors.text.tertiary,
      display: 'flex',
      alignItems: 'center',
      gap: designTokens.spacing.xs
    },
    buttonGroup: {
      display: 'flex',
      gap: designTokens.spacing.md
    },
    primaryButton: {
      ...commonStyles.buttonBase,
      backgroundColor: designTokens.colors.primary,
      color: designTokens.colors.surface,
      padding: `${designTokens.spacing.md} ${designTokens.spacing.xl}`,
      fontSize: '14px',
      fontWeight: '600'
    },
    secondaryButton: {
      ...commonStyles.buttonBase,
      backgroundColor: designTokens.colors.surface,
      color: designTokens.colors.secondary,
      border: `1px solid ${designTokens.colors.border}`,
      padding: `${designTokens.spacing.md} ${designTokens.spacing.xl}`,
      fontSize: '14px',
      fontWeight: '600'
    },
    dangerButton: {
      ...commonStyles.buttonBase,
      backgroundColor: designTokens.colors.danger,
      color: designTokens.colors.surface,
      padding: `${designTokens.spacing.md} ${designTokens.spacing.xl}`,
      fontSize: '14px',
      fontWeight: '600'
    }
  };

  //= 문의 사항 상세 내역 불러오기
  const getDetail = async () => {
    if (prevent) return;
    prevent = true;
    setTimeout(() => {
      prevent = false;
    }, 200);
    const result = await getInquiryDetail(inquiryId);
    if (typeof result === "object") {
      const {
        context,
        user_name,
        title,
        answer,
        created_at,
        status_flag,
        user_id,
        name,
        phone,
        email,
        inquiry_type,
        inquiry_reason,
        apply_type,
        applicant_type,
        mark_name,
        files,
        is_auto,
      } = result?.data;
      setInfo({
        user_id: user_id,
        user_name: user_name,
        created_at: created_at,
        title: title,
        context: context,
        answer: answer,
        status: status_flag,
        name: name,
        phone: phone,
        email: email,
        inquiry_type: inquiry_type,
        inquiry_reason: inquiry_reason,
        apply_type: apply_type,
        applicant_type: applicant_type,
        mark_name: mark_name,
        files: files || [],
        is_auto: is_auto || false,
      });
    } else return await catchError(result, navigate, setAlertBox, setAlert);
  };

  //= 답변 등록 / 수정 / 삭제
  const answerHandlingFn = async (mode) => {
    if (!info.answer)
      return commonModalSetting(
        setAlertBox,
        true,
        "alert",
        "답변을 작성해 주세요."
      );
    let answer;
    if (mode === "delete") answer = "";
    else answer = info.answer;
    const data = { answer: answer };
    const result = await answerHandling(inquiryId, data);
    if (typeof result === "object") {
      if (mode === "apply") {
        changeState(setInfo, "status", true);
      } else if (mode === "edit") setEdit(false);
      else {
        setInfo((prev) => {
          const clone = { ...prev };
          clone.status = false;
          clone.answer = "";
          return clone;
        });
        setAlert("deleteComplete");
        commonModalSetting(
          setAlertBox,
          true,
          "alert",
          "정상적으로 삭제되었습니다."
        );
      }
      getDetail();
    } else return await catchError(result, navigate, setAlertBox, setAlert);
  };

  // 원본 파일 다운로드 받기
  const handleFileDownload = async () => {
    if (!info.files[0]?.original_file_name) {
      window.alert("다운로드할 파일이 없습니다.");
      return;
    }

    const fileInfo = info.files[0];

    try {
      const response = await getFileDownload(
        fileInfo.file_name,
        fileInfo.original_file_name
      );

      const blob = new Blob([response.data], {
        type: response.headers["content-type"],
      });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = fileInfo.original_file_name;
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("파일 다운로드 실패:", error);
      window.alert("파일 다운로드 실패했습니다. 다시 시도해주세요.");
    }
  };

  const [, setFileType] = useState("unknown");

  // 첨부된 파일 타입확인
  useEffect(() => {
    if (!info.files?.original_file_name) return;

    const extension = info.files?.original_file_name.split(".").pop();
    const imgExts = ["jpg", "jpeg", "png", "gif", "bmp", "webp"];

    if (imgExts.includes(extension)) {
      setFileType("image");
    } else if (extension === "pdf") {
      setFileType("pdf");
    } else {
      setFileType("unknown");
    }
  }, [info.files?.original_file_name]);

  useEffect(() => {
    byteCount(info.answer, setInfo, setByte, "answer", 3000);
  }, [info.answer]);

  // 문의 유형 옵션 렌더링
  const renderInquiryTypeOptions = () => {
    const options = [
      "등록가능성 문의",
      "상표문의", 
      "상표등록신청",
      "기타(서비스 불편사항)"
    ];

    return (
      <div style={modalStyles.inquiryTypeOptions}>
        {options.map((option) => (
          <div
            key={option}
            style={{
              ...modalStyles.radioOption,
              ...(info.inquiry_type === option ? modalStyles.radioOptionSelected : {})
            }}
          >
            <span>{option}</span>
          </div>
        ))}
      </div>
    );
  };

  useEffect(() => {
    if (!alertBox.bool) {
      getDetail();
    }
  }, [alertBox.bool]);


  return (
    <>
      <div style={modalStyles.overlay}>
        <div style={modalStyles.container} onClick={(e) => e.stopPropagation()}>
          {/* 헤더 */}
          <div style={modalStyles.header}>
            <h2 style={modalStyles.title}>
              <FaQuestionCircle />
              문의 사항 상세 내역
              {info.is_auto && (
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    padding: "2px 10px",
                    borderRadius: "4px",
                    fontSize: "12px",
                    fontWeight: "600",
                    backgroundColor: "#fee2e2",
                    color: "#b91c1c",
                    marginLeft: "8px",
                  }}
                >
                  임의글
                </span>
              )}
            </h2>
            <button
              style={modalStyles.closeButton}
              onClick={() => setModal(false)}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = designTokens.colors.background;
                e.target.style.color = designTokens.colors.text.primary;
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = designTokens.colors.text.tertiary;
              }}
            >
              <FaTimes size={16} />
            </button>
          </div>

          {/* 컨텐츠 */}
          <div style={modalStyles.content}>
            {/* 상태 헤더 */}
            <div style={modalStyles.statusHeader}>
              <div style={modalStyles.dateInfo}>
                <FaCalendar size={12} />
                작성일: {info.created_at.replace("T", " ")}
              </div>
              <div style={{
                ...modalStyles.memberBadge,
                ...(info.user_id ? badgeStyles.primary : badgeStyles.neutral)
              }}>
                <FaUser size={10} />
                {info.user_id ? "회원" : "비회원"}
              </div>
            </div>

            {/* 문의 유형 */}
            <div style={modalStyles.section}>
              <h3 style={modalStyles.sectionTitle}>
                <FaTag />
                문의 유형
              </h3>
              {renderInquiryTypeOptions()}
            </div>

            {/* 사용자 정보 */}
            <div style={modalStyles.section}>
              <h3 style={modalStyles.sectionTitle}>
                <FaUser />
                사용자 정보
              </h3>
              <div style={modalStyles.infoGrid}>
                <div style={modalStyles.infoRow}>
                  <div style={modalStyles.infoLabel}>
                    <FaUser size={12} />
                    작성자
                  </div>
                  <div style={modalStyles.infoValue}>{info.user_name}</div>
                </div>
                <div style={modalStyles.infoRow}>
                  <div style={modalStyles.infoLabel}>
                    <FaTag size={12} />
                    상표/상호명
                  </div>
                  <div style={modalStyles.infoValue}>{info.mark_name}</div>
                </div>
                <div style={modalStyles.infoRow}>
                  <div style={modalStyles.infoLabel}>
                    <FaIdCard size={12} />
                    사용자 ID
                  </div>
                  <div style={modalStyles.infoValue}>{info.user_id}</div>
                </div>
                <div style={modalStyles.infoRow}>
                  <div style={modalStyles.infoLabel}>
                    <FaPhone size={12} />
                    전화번호
                  </div>
                  <div style={modalStyles.infoValue}>{info.phone}</div>
                </div>
                <div style={modalStyles.infoRow}>
                  <div style={modalStyles.infoLabel}>
                    <FaEnvelope size={12} />
                    이메일
                  </div>
                  <div style={modalStyles.infoValue}>{info.email}</div>
                </div>
                <div style={modalStyles.infoRow}>
                  <div style={modalStyles.infoLabel}>
                    <FaTag size={12} />
                    유입 경로
                  </div>
                  <div style={modalStyles.infoValue}>{info.inquiry_reason}</div>
                </div>
              </div>
            </div>

            {/* 상표등록 문의 상세 */}
            {info.inquiry_type === "상표등록신청" && (
              <div style={modalStyles.section}>
                <h3 style={modalStyles.sectionTitle}>
                  <FaBuilding />
                  상표등록 상세 정보
                </h3>
                <div style={modalStyles.inquiryTypeOptions}>
                  <div style={{
                    ...modalStyles.radioOption,
                    ...(info.applicant_type === "개인" ? modalStyles.radioOptionSelected : {})
                  }}>
                    <FaUser size={12} />
                    개인
                  </div>
                  <div style={{
                    ...modalStyles.radioOption,
                    ...(info.applicant_type === "법인" ? modalStyles.radioOptionSelected : {})
                  }}>
                    <FaBuilding size={12} />
                    법인
                  </div>
                </div>
                <div style={modalStyles.inquiryTypeOptions}>
                  {["일반출원대행", "조사출원대행", "신속출원대행", "신속조사출원대행"].map((option) => (
                    <div
                      key={option}
                      style={{
                        ...modalStyles.radioOption,
                        ...(info.apply_type === option ? modalStyles.radioOptionSelected : {})
                      }}
                    >
                      <div style={modalStyles.radioCircle}></div>
                      <span>{option}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 문의 내용 */}
            <div style={modalStyles.questionSection}>
              <div style={modalStyles.questionHeader}>
                <div style={modalStyles.questionTitle}>
                  문의 제목: {info.title}
                </div>
                {info.files[0]?.original_file_name && (
                  <button
                    style={modalStyles.downloadButton}
                    onClick={handleFileDownload}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = designTokens.colors.successHover || '#047857';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = designTokens.colors.success;
                    }}
                  >
                    <FaDownload size={10} />
                    첨부 파일 다운로드
                  </button>
                )}
              </div>
              <div style={modalStyles.questionContent}>
                {info.context.split("\n").map((line, idx) => (
                  <React.Fragment key={idx}>
                    {line}
                    <br />
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* 답변 섹션 */}
            <div style={modalStyles.answerSection}>
              <h3 style={modalStyles.sectionTitle}>
                <BsFillQuestionCircleFill />
                {info.status ? "답변 내용" : "답변 작성"}
              </h3>
              
              {info.status ? (
                // 답변이 있을 때
                <>
                  {edit ? (
                    <textarea
                      style={modalStyles.answerTextarea}
                      value={info.answer}
                      onChange={(e) => changeState(setInfo, "answer", e.target.value)}
                    />
                  ) : (
                    <div style={modalStyles.answerContent}>
                      {info.answer}
                    </div>
                  )}
                  
                  <div style={modalStyles.footer}>
                    <div style={modalStyles.byteCount}>
                      <FaFileAlt size={12} />
                      <span>{byte} / 3000 bytes</span>
                    </div>
                    <div style={modalStyles.buttonGroup}>
                      {edit ? (
                        <>
                          <button
                            style={modalStyles.primaryButton}
                            onClick={() => answerHandlingFn("edit")}
                          >
                            <FaCheck size={12} />
                            답변 완료
                          </button>
                          <button
                            style={modalStyles.secondaryButton}
                            onClick={() => {
                              setAlert("edit");
                              commonModalSetting(
                                setAlertBox,
                                true,
                                "confirm",
                                "정말 취소하시겠습니까?<br/>지금까지 수정된 내용은 반영되지 않습니다."
                              );
                            }}
                          >
                            답변 취소
                          </button>
                        </>
                      ) : (
                        <button
                          style={modalStyles.primaryButton}
                          onClick={() => setEdit(true)}
                        >
                          <FaEdit size={12} />
                          답변 수정
                        </button>
                      )}
                      <button
                        style={modalStyles.dangerButton}
                        onClick={() => {
                          setAlert("delete");
                          commonModalSetting(
                            setAlertBox,
                            true,
                            "confirm",
                            "답변을 삭제하시면 다시 복구할 수 없습니다.<br/>답변을 삭제하시겠습니까?"
                          );
                        }}
                      >
                        <FaTrash size={12} />
                        답변 삭제
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                // 답변이 없을 때
                <>
                  <textarea
                    style={modalStyles.answerTextarea}
                    value={info.answer}
                    onChange={(e) => changeState(setInfo, "answer", e.target.value)}
                    placeholder="답변을 작성해주세요..."
                  />
                  <div style={modalStyles.footer}>
                    <div style={modalStyles.byteCount}>
                      <FaFileAlt size={12} />
                      <span>{byte} / 3000 bytes</span>
                    </div>
                    <button
                      style={modalStyles.primaryButton}
                      onClick={() => answerHandlingFn("apply")}
                    >
                      <FaCheck size={12} />
                      답변완료
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {alertBox.bool && (
        <CommonModal
          setModal={setAlertBox}
          modal={alertBox}
          okFn={() => {
            if (alert === "edit") setEdit(false);
            else if (alert === "deleteComplete") getDetail();
            else if (alert === "logout") navigate("/");
            else {
              answerHandlingFn("delete");
            }
          }}
        />
      )}
    </>
  );
};

export default InquiryDetail;